import {
  Avatar,
  Button,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  Theme,
  Tooltip,
  createStyles,
  createSvgIcon,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {
  BarChartRounded,
  CalendarToday,
  Announcement,
  Dashboard,
  ExitToApp,
  ExpandLess,
  ExpandMore,
  FolderOpen,
  Build,
  ChevronLeft,
  ChevronRight,
  Security,
  Mood,
  Gavel,
  Group,
  NoteAdd,
  Apps,
  Business,
  PeopleAlt,
  MonetizationOn,
} from '@material-ui/icons';
import classNames from 'classnames';
import { RouterNavLink } from 'components';
import brandLogo from 'layouts/App/assets/brand-kt.svg';
import { logout } from 'lib';
import { getUserData } from 'lib/Helper/Auth';
import * as React from 'react';
import WellBeingMessage from './WellBeingMessage';

// Material's Science icon, which @material-ui/icons v4 predates
const Science = createSvgIcon(
  <path d="M19.8 18.4 14 10.67V6.5l1.35-1.69c.26-.33.03-.81-.39-.81H9.04c-.42 0-.65.48-.39.81L10 6.5v4.17L4.2 18.4c-.49.66-.02 1.6.8 1.6h14c.82 0 1.29-.94.8-1.6z" />,
  'Science',
);

// Mirrors the Talexio HR sidebar (talexio-hr-frontend layouts/App/components/Sidebar)
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawer: {
      width: (props: { collapsed: boolean }) =>
        props.collapsed ? 80 : theme.drawerWidth,
      backgroundColor: grey[100],
      overflowX: 'hidden',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      marginTop: theme.spacing(1),
      padding: theme.spacing(2, 3),
    },
    logoImage: {
      display: 'block',
      width: '100%',
      height: 'auto',
    },
    user: {
      // ListItemText grows by default, which would steal height from the nav list
      flex: 'none',
      margin: 0,
      padding: theme.spacing(0, 3, 1),
    },
    avatar: {
      width: 32,
      height: 32,
      borderRadius: 6,
    },
    list: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      '&::-webkit-scrollbar': {
        width: theme.spacing(1),
      },
      '&::-webkit-scrollbar-track': {
        background: 'none',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: grey[300],
        borderRadius: theme.spacing(1),
      },
    },
    divider: {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
    item: {
      transition: 'all 200ms linear',
      margin: theme.spacing(0.5, 1),
      padding: theme.spacing(0.5, 2),
      height: 33,
      boxSizing: 'border-box',
      borderRadius: 3,
      width: 'auto',
      color: grey[700],
      '&:hover, &:focus': {
        color: theme.palette.getContrastText(theme.palette.primary.light),
        backgroundColor: theme.palette.primary.light,
        '& $itemIcon': {
          color: theme.palette.getContrastText(theme.palette.primary.light),
        },
      },
    },
    itemSmall: {
      padding: theme.spacing(0.5, 0),
      justifyContent: 'center',
    },
    subItem: {
      paddingLeft: theme.spacing(4),
    },
    itemActive: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      boxShadow: 'none',
      '&$itemActive $itemIcon': {
        color: theme.palette.primary.contrastText,
      },
      '&:hover, &:focus': {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        '& $itemIcon': {
          color: theme.palette.primary.contrastText,
        },
      },
    },
    itemIcon: {
      // Doubled class beats MUI's own .MuiListItemIcon-root color and minWidth
      '&$itemIcon': {
        color: 'rgba(0, 0, 0, 0.57)',
        minWidth: 'unset',
        marginRight: theme.spacing(3),
        display: 'flex',
        alignItems: 'center',
      },
    },
    itemIconSmall: {
      justifyContent: 'center',
      '&$itemIconSmall': {
        marginRight: 0,
      },
    },
    itemText: {
      color: 'inherit',
    },
    wellbeing: {
      padding: theme.spacing(1.5, 2, 1.5, 3),
      borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    },
    arrow: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: (props: { collapsed: boolean }) =>
        props.collapsed ? 'center' : 'flex-end',
    },
    fullWidth: {
      width: '100%',
    },
  }),
);

type MenuItemType = 'link' | 'accordion';

interface BaseMenuItem {
  type: MenuItemType;
  key: string;
  permissions?: string[];
}

interface LinkMenuItem extends BaseMenuItem {
  type: 'link';
  path: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

interface AccordionMenuItem extends BaseMenuItem {
  type: 'accordion';
  label: string;
  icon: React.ReactNode;
  children: LinkMenuItem[];
}

type MenuItem = LinkMenuItem | AccordionMenuItem;

interface MenuGroup {
  subheader?: string;
  items: MenuItem[];
}

interface Props {
  openDrawer: () => void;
  closeDrawer: () => void;
  open: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
}

export const Sidebar: React.FC<Props> = ({
  open,
  openDrawer,
  closeDrawer,
  onCollapseChange,
}) => {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [collapsed, setCollapsed] = React.useState(false);
  const classes = useStyles({ collapsed });
  const [openAccordions, setOpenAccordions] = React.useState<
    Record<string, boolean>
  >({});

  React.useEffect(() => {
    if (isLg && open) {
      closeDrawer();
    }
    if (!isLg && collapsed) {
      setCollapsed(false);
    }
  }, [isLg, open, closeDrawer, collapsed]);

  React.useEffect(() => {
    onCollapseChange?.(collapsed);
  }, [collapsed, onCollapseChange]);

  const user = getUserData();
  if (!user) {
    return null;
  }
  const userPermissions = user.permissions ?? [];

  const handleAccordionClick = (key: string) => {
    // When the rail is collapsed there is no room to reveal children, so
    // expand it first and open the accordion in one action — otherwise the
    // accordion's items are unreachable in collapsed mode.
    if (collapsed) {
      setCollapsed(false);
      setOpenAccordions((prev) => ({ ...prev, [key]: true }));
      return;
    }
    setOpenAccordions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLinkClick = () => {
    if (!isLg) {
      closeDrawer();
    }
  };

  const handleToggleCollapse = () => {
    if (isLg) {
      setCollapsed(!collapsed);
    }
  };

  const menuGroups: MenuGroup[] = [
    {
      subheader: 'Work',
      items: [
        {
          type: 'link',
          key: 'dashboard',
          path: '/dashboard',
          label: 'Dashboard',
          icon: <Dashboard />,
        },
        {
          type: 'link',
          key: 'clients',
          path: '/clients',
          label: 'Our Clients',
          icon: <Group />,
          permissions: ['read'],
        },
        {
          type: 'link',
          key: 'deals',
          path: '/deals',
          label: 'Deals',
          icon: <MonetizationOn />,
        },
        {
          type: 'link',
          key: 'meetings',
          path: '/meetings',
          label: 'Client Meetings',
          icon: <CalendarToday />,
        },
      ],
    },
    {
      subheader: 'Insights',
      items: [
        {
          type: 'link',
          key: 'dashboards',
          path: '/dashboards',
          label: 'Dashboard Gallery',
          icon: <BarChartRounded />,
        },
        {
          type: 'link',
          key: 'nps',
          path: '/nps',
          label: 'Net Promoter Scores',
          icon: <Mood />,
        },
        {
          type: 'link',
          key: 'brands',
          path: '/brands',
          label: 'Our Brands',
          icon: <Business />,
        },
      ],
    },
    {
      subheader: 'Tools',
      items: [
        {
          type: 'link',
          key: 'resources',
          path: '/resources',
          label: 'Tools & Resources',
          icon: <Build />,
        },
        {
          type: 'link',
          key: 'playground',
          path: '/playground',
          label: 'Playground',
          icon: <Science />,
          permissions: [
            'interview_report_generator',
            'cv_critique',
            'vacancy_matcher',
          ],
        },
        {
          type: 'accordion',
          key: 'management',
          label: 'Workspace',
          icon: <Apps />,
          children: [
            {
              type: 'link',
              key: 'folders',
              path: '/drive-folders',
              label: 'Drive Folders',
              icon: <FolderOpen />,
              permissions: ['create_folders'],
            },
            {
              type: 'link',
              key: 'agreements',
              path: '/drive-terms',
              label: 'Agreements',
              icon: <Gavel />,
            },
            {
              type: 'link',
              key: 'proposals',
              path: '/proposal-generator',
              label: 'Talexio Proposals',
              icon: <NoteAdd />,
              permissions: ['generate_proposal'],
            },
            {
              type: 'link',
              key: 'client-allocation',
              path: '/commercial-support-utility',
              label: 'Client Allocation',
              icon: <PeopleAlt />,
              permissions: ['client_allocation'],
            },
          ],
        },
      ],
    },
    {
      subheader: 'Admin',
      items: [
        {
          type: 'link',
          key: 'permissions',
          path: '/permissions',
          label: 'Permissions',
          icon: <Security />,
          permissions: ['manage_permissions'],
        },
      ],
    },
    {
      items: [
        {
          type: 'link',
          key: 'feedback',
          path: '/feedback',
          label: 'Talk To Us',
          icon: <Announcement />,
        },
      ],
    },
  ];

  const accountConfig: MenuItem[] = [
    {
      type: 'link',
      key: 'logout',
      path: '#',
      label: 'Logout',
      icon: <ExitToApp />,
      onClick: logout,
    },
  ];

  const hasPermission = (item: MenuItem): boolean => {
    if (!item.permissions || item.permissions.length === 0) return true;
    return item.permissions.some((p) => userPermissions.includes(p));
  };

  const filterMenu = (items: MenuItem[]): MenuItem[] => {
    return items.reduce<MenuItem[]>((acc, item) => {
      if (!hasPermission(item)) return acc;
      if (item.type === 'accordion') {
        const visibleChildren = item.children.filter(hasPermission);
        if (visibleChildren.length > 0) {
          acc.push({ ...item, children: visibleChildren });
        }
      } else {
        acc.push(item);
      }
      return acc;
    }, []);
  };

  const visibleGroups = menuGroups
    .map((group) => ({ ...group, items: filterMenu(group.items) }))
    .filter((group) => group.items.length > 0);
  const visibleAccountItems = filterMenu(accountConfig);

  const itemClassName = (...extra: string[]) =>
    classNames(classes.item, ...extra, { [classes.itemSmall]: collapsed });

  const itemIcon = (label: string, icon: React.ReactNode) => {
    const node = (
      <ListItemIcon
        className={classNames(classes.itemIcon, {
          [classes.itemIconSmall]: collapsed,
        })}
      >
        {icon as React.ReactElement}
      </ListItemIcon>
    );
    return collapsed ? (
      <Tooltip placement="right" title={label}>
        {node}
      </Tooltip>
    ) : (
      node
    );
  };

  const itemText = (label: string) =>
    !collapsed && (
      <ListItemText
        secondary={label}
        secondaryTypographyProps={{ className: classes.itemText }}
      />
    );

  const renderMenuItem = (item: MenuItem) => {
    switch (item.type) {
      case 'link':
        if (item.key === 'logout') {
          return (
            <ListItem
              button
              key={item.key}
              className={itemClassName()}
              aria-label={item.label}
              onClick={() => {
                if (item.onClick) item.onClick();
                handleLinkClick();
              }}
            >
              {itemIcon(item.label, item.icon)}
              {itemText(item.label)}
            </ListItem>
          );
        }

        return (
          <ListItem
            button
            key={item.key}
            component={RouterNavLink}
            to={item.path}
            activeClassName={classes.itemActive}
            className={itemClassName()}
            aria-label={item.label}
            onClick={() => {
              if (item.onClick) item.onClick();
              handleLinkClick();
            }}
          >
            {itemIcon(item.label, item.icon)}
            {itemText(item.label)}
          </ListItem>
        );

      case 'accordion':
        const isOpen = openAccordions[item.key] || false;
        return (
          <React.Fragment key={item.key}>
            <ListItem
              button
              onClick={() => handleAccordionClick(item.key)}
              className={itemClassName()}
              aria-label={item.label}
              aria-expanded={isOpen}
            >
              {itemIcon(item.label, item.icon)}
              {itemText(item.label)}
              {!collapsed && (isOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItem>
            {!collapsed && (
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding dense>
                  {item.children.map((child) => (
                    <ListItem
                      button
                      key={child.key}
                      component={RouterNavLink}
                      to={child.path}
                      activeClassName={classes.itemActive}
                      className={itemClassName(classes.subItem)}
                      onClick={handleLinkClick}
                    >
                      {itemIcon(child.label, child.icon)}
                      {itemText(child.label)}
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        );

      default:
        return null;
    }
  };

  const navGroups = [...visibleGroups.map((g) => g.items), visibleAccountItems];

  return (
    <SwipeableDrawer
      anchor="left"
      ModalProps={{ keepMounted: true }}
      variant={isLg ? 'permanent' : 'temporary'}
      open={isLg || open}
      onClose={closeDrawer}
      onOpen={openDrawer}
      classes={{ paper: classes.drawer }}
    >
      <div className={classes.logo}>
        {collapsed ? (
          <Avatar className={classes.avatar}>K</Avatar>
        ) : (
          <img
            src={brandLogo}
            alt="KonnektTalexio"
            className={classes.logoImage}
          />
        )}
      </div>

      {!collapsed && (
        <ListItemText
          className={classes.user}
          primary={user.name}
          primaryTypographyProps={{ noWrap: true }}
          secondary={`${user.title} | ${user.group}`}
          secondaryTypographyProps={{ noWrap: true }}
        />
      )}

      <List dense className={classes.list}>
        {navGroups.map((items, index) => (
          <React.Fragment key={index}>
            {index > 0 && <Divider className={classes.divider} />}
            {items.map(renderMenuItem)}
          </React.Fragment>
        ))}
      </List>

      {!collapsed && (
        <div className={classes.wellbeing}>
          <WellBeingMessage />
        </div>
      )}

      <div className={classes.arrow}>
        <Divider className={classNames(classes.divider, classes.fullWidth)} />
        {isLg && (
          <Button
            onClick={handleToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={classNames({ [classes.fullWidth]: collapsed })}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </Button>
        )}
      </div>
    </SwipeableDrawer>
  );
};
